"use client";

import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useCampaign } from "./CampaignProvider";

// framer-motion only loads once this zone has actually had a campaign (see
// below) — most zone mounts on most pages have none.
const CampaignZoneAnimatedRenderer = dynamic(() => import("./CampaignZoneAnimatedRenderer"), {
  ssr: false,
});

interface CampaignZoneProps {
  /** Must match a campaign's `zone` field and the shared zone whitelist (CAMPAIGN_ZONES in services/campaign.ts). */
  name: string;
}

/**
 * Inline mount point for `banner` campaigns — pages embed this directly in
 * their own layout flow wherever a Banner should physically appear. Reads
 * its own scope from the shared CampaignProvider; never fetches on its own,
 * so multiple zones on one page never cause extra API requests. Renders
 * nothing when the zone has no eligible campaigns, is unknown, or receives
 * stale data for a zone that no longer exists — never throws.
 */
export const CampaignZone: React.FC<CampaignZoneProps> = ({ name }) => {
  const { zoneCampaigns } = useCampaign();
  const campaigns = zoneCampaigns[name] ?? [];
  // Mirrors CampaignProvider's hasHadActiveCampaign pattern: once this zone
  // has had a campaign, keep its animated renderer mounted so exit
  // animations can still play when it empties back out.
  const [hasHadCampaigns, setHasHadCampaigns] = useState(false);

  useEffect(() => {
    if (campaigns.length > 0) {
      setHasHadCampaigns(true);
    }
  }, [campaigns.length]);

  if (!hasHadCampaigns) {
    return null;
  }

  return <CampaignZoneAnimatedRenderer zone={name} campaigns={campaigns} />;
};

export default CampaignZone;
